import {
    GeometryCollectionType,
    MapTheme,
    SavedView,
    useCreateSavedViewMutation,
    useUpdateSavedViewMutation,
} from "@biggeo/bg-server-lib/studio";
import {
    Box,
    Button,
    MultilineTextField,
    Stack,
    SubmittingButton,
    TextField,
    Typography,
} from "@biggeo/bg-ui/lab";
import { Formik, FormikHelpers } from "formik";
import isString from "lodash/isString";
import Zod from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { Routes } from "../../../navigation/redux/model";
import {
    useErrorToast,
    useSuccessToast,
} from "../../../toaster/containers/redux/hooks";
import { useScreenshot } from "../hooks/other-hooks";
import { ISaveView } from "../views/SaveView";
import isEmpty from "lodash/isEmpty";
import { MapContextPolygon } from "../context/types";
import { getInputGeometry } from "../../utils/geometry-utils";
import { getSavedViewInputDatasets } from "../../filter-criteria/utils/utils";

interface ISaveViewForm
    extends Pick<
        ISaveView,
        "bounds" | "viewport" | "setOpenSaveViewPopper" | "datasets"
    > {
    readonly mapTemplateId: string;
    readonly savedView?: SavedView;
    readonly polygons: MapContextPolygon[];
    readonly style?: MapTheme;
}

const SaveViewForm = ({
    bounds,
    viewport,
    setOpenSaveViewPopper,
    polygons,
    mapTemplateId,
    savedView,
    style,
    datasets,
}: ISaveViewForm) => {
    const errorToast = useErrorToast();
    const successToast = useSuccessToast();
    const {
        executeMutation: createSavedView,
        mutationReturn: [_c, { loading: cloading }],
    } = useCreateSavedViewMutation();

    const {
        executeMutation: updateSavedView,
        mutationReturn: [_u, { loading: uloading }],
    } = useUpdateSavedViewMutation();

    const { getScreenshot, loading } = useScreenshot();

    const openNewTab = (url: string) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const getSavedViewScreenshot = async (htmlid: string) => {
        return await getScreenshot({
            download: false,
            elementId: htmlid,
            onError: (message) => errorToast(message),
            options: {
                scale: 0.25,
            },
        });
    };

    const savedViewDatasets = getSavedViewInputDatasets({ datasets });

    const savedViewport = bounds
        ? {
              latBounds: {
                  min: bounds._sw.lat,
                  max: bounds._ne.lat,
              },
              lngBounds: {
                  min: bounds._sw.lng,
                  max: bounds._ne.lng,
              },
          }
        : viewport;

    const geometries = polygons.map((p) => getInputGeometry(p.feature));

    const handleSaveView = async (
        values: Partial<SavedView>,
        actions: FormikHelpers<Partial<SavedView>>
    ) => {
        const screenshot = await getSavedViewScreenshot("map");

        if (isString(screenshot)) {
            if (values.id) {
                updateSavedView({
                    variables: {
                        input: {
                            id: values.id,
                            name: values.name,
                            description: values.description,
                            viewport: savedViewport,
                            img: screenshot,
                            datasets: savedViewDatasets,
                            fkMapTemplateId: mapTemplateId,
                            savedArea: {
                                fkSavedAreaId: savedView?.fkSavedAreaId,
                                geometries: {
                                    type: GeometryCollectionType.GeometryCollection,
                                    geometries,
                                },
                            },
                            theme: style,
                        },
                    },
                    onCompleted: (_data) => {
                        successToast("Saved view updated successfully");
                        setOpenSaveViewPopper?.(false);
                    },
                    onError: (e) => {
                        errorToast(e.message);
                    },
                });
            } else {
                createSavedView({
                    variables: {
                        input: {
                            name: values.name || "",
                            description: values.description,
                            viewport: savedViewport,
                            fkMapTemplateId: mapTemplateId,
                            datasets: savedViewDatasets,
                            geometries: isEmpty(geometries)
                                ? undefined
                                : {
                                      type: GeometryCollectionType.GeometryCollection,
                                      geometries,
                                  },
                            img: screenshot,
                            theme: style || MapTheme.light,
                            isExport: false,
                        },
                    },
                    onError: (err) => errorToast(err.message),
                    onCompleted: (data) => {
                        actions.resetForm();
                        setOpenSaveViewPopper?.(false);

                        successToast("New view saved successfully.", {
                            additionalAction: {
                                ctaText: "Open",
                                onClick: () =>
                                    openNewTab(
                                        `${Routes.mapView}/${mapTemplateId}/map?savedViewId=${data.createSavedView.id}`
                                    ),
                            },
                        });
                    },
                });
            }
        }
    };

    const initialValues = {
        id: savedView?.id,
        name: savedView?.name || "",
        description: savedView?.description || "",
    };
    return (
        <Formik<Partial<SavedView>>
            initialValues={initialValues}
            onSubmit={(values, actions) => {
                handleSaveView(values, actions);
            }}
            validationSchema={toFormikValidationSchema(
                Zod.object({
                    id: Zod.string().nullable().optional(),
                    name: Zod.string(),
                    description: Zod.string().optional(),
                })
            )}
            validateOnMount
        >
            {({ values, setValues, resetForm, handleSubmit, isValid }) => {
                const onChange = (i: Partial<SavedView>) => {
                    setValues((p) => ({ ...p, ...i }));
                };
                return (
                    <Stack
                        gap={2}
                        sx={{
                            padding: 4,
                            backgroundColor: (theme) =>
                                theme.palette.background.main,
                            width: 80,
                            borderRadius: (theme) => theme.spacing(1),
                        }}
                    >
                        <Stack>
                            <Typography variant="body2" fontWeight="bold">
                                {`Save ${savedView?.id ? "Changes" : "View"}`}
                            </Typography>
                            <Typography
                                variant="body3"
                                sx={{
                                    color: (theme) =>
                                        theme.palette.disabled.onContainer,
                                }}
                            >
                                {`${savedView?.id ? "Update" : "Enter"} details
                            below`}
                            </Typography>
                        </Stack>
                        <TextField
                            label="Name"
                            fullWidth
                            required
                            value={values.name || ""}
                            onChange={(_, v) =>
                                onChange({
                                    name: v === "" ? undefined : v,
                                })
                            }
                        />
                        <MultilineTextField
                            label="Description"
                            subLabel="(Optional)"
                            fullWidth
                            value={values.description || ""}
                            onChange={(_, v) =>
                                onChange({
                                    description: v === "" ? undefined : v,
                                })
                            }
                        />
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                alignSelf: "end",
                            }}
                        >
                            <Button
                                density="dense"
                                variant="outlined"
                                onClick={() => {
                                    setOpenSaveViewPopper?.(false);
                                    resetForm();
                                }}
                                disabled={loading || cloading || uloading}
                                sx={{
                                    display:
                                        loading || cloading || uloading
                                            ? "none"
                                            : "flex",
                                }}
                            >
                                Cancel
                            </Button>
                            <SubmittingButton
                                loading={loading || cloading || uloading}
                                disabled={!isValid}
                                density="dense"
                                type="saving"
                                onClick={() => handleSubmit()}
                            >
                                {`Save ${savedView?.id ? "Changes" : "View"}`}
                            </SubmittingButton>
                        </Box>
                    </Stack>
                );
            }}
        </Formik>
    );
};

export default SaveViewForm;
